/* eslint-disable ember/no-computed-properties-in-native-classes */
import { action, computed } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismTable } from '@mylighthouse/prism-ember';
import SettingsReportsTablesCellsTdCta from 'lumen/components/settings/reports/tables/cells/td-cta';
import SettingsReportsTablesCellsTdFilters from 'lumen/components/settings/reports/tables/cells/td-filters';
import SettingsReportsTablesCellsTdFrequency from 'lumen/components/settings/reports/tables/cells/td-frequency';
import SettingsReportsTablesCellsTdHotelOrMarket from 'lumen/components/settings/reports/tables/cells/td-hotel-or-market';
import SettingsReportsTablesCellsTdNbOfRecipients from 'lumen/components/settings/reports/tables/cells/td-nb-of-recipients';
import SettingsReportsTablesCellsTdReportsType from 'lumen/components/settings/reports/tables/cells/td-reports-type';
import SettingsReportsTablesCellsTdScheduledBy from 'lumen/components/settings/reports/tables/cells/td-scheduled-by';

const REPORTS_COLUMNS = {
  SELECT_FOR_DELETE: 'select-for-delete',
  NAME: 'name',
  TYPE: 'type',
  FILTERS: 'filters',
  FREQUENCY: 'frequency',
  SCHEDULED_BY: 'scheduledBy',
  CREATED_BY: 'created_by',
  NR_OF_RECEIVERS: 'nr_of_receivers',
  CTA: 'cta',
};

interface SettingsReportsTablesTableReportsSignature {
  Args: {
    isLoading: unknown;
    message: unknown;
    model: unknown;
    name: unknown;
    rowSelection: unknown;
    setRowSelection: unknown;
    sortBy: unknown;
    sortDirection: unknown;
    sortResults: unknown;
  };
}

export default class SettingsReportsTablesTableReports extends Component<SettingsReportsTablesTableReportsSignature> {
  @service declare intl: Services['intl'];

  @computed('args.{model.[],model,sortBy,sortDirection}')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get columns() {
    // @ts-expect-error: Incorrect type
    const columns = [];
    const tableColumns = [
      {
        name: this.intl.t(`reports.scheduled_reports.hotels`),
        tdComponent: SettingsReportsTablesCellsTdHotelOrMarket,
        thClassNames:
          'settings/reports/tables/cells/text-data settings-header-space-between-text',
        width: 300,
        textAlign: 'left',
        altDisplayName: this.intl.t(
          `reports.scheduled_reports.hotels_or_markets`,
        ),
        displayValuePath: 'reportGroup',
        valuePath: `${REPORTS_COLUMNS.NAME}.hotel.id`,
        isSortable: true,
        columnKey: REPORTS_COLUMNS.NAME,
      },
      {
        name: this.intl.t(`reports.scheduled_reports.type`),
        tdComponent: SettingsReportsTablesCellsTdReportsType,
        width: 125,
        minWidth: 125,
        textAlign: 'left',
        thClassNames: 'text-data settings-header-space-between-text',
        valuePath: REPORTS_COLUMNS.TYPE,
        isSortable: true,
        columnKey: REPORTS_COLUMNS.TYPE,
      },
      {
        name: this.intl.t(`reports.scheduled_reports.filters`),
        tdComponent: SettingsReportsTablesCellsTdFilters,
        textAlign: 'left',
        thClassNames:
          'text-data settings-reports-filters-col settings-header-space-between-text',
        tdClassNames: 'text-data settings-reports-filters-col',
        valuePath: REPORTS_COLUMNS.FILTERS,
        displayValuePath: 'reportGroup',
        isSortable: true,
        width: 200,
        columnKey: REPORTS_COLUMNS.FILTERS,
      },
      {
        name: this.intl.t(`reports.scheduled_reports.frequency`),
        tdComponent: SettingsReportsTablesCellsTdFrequency,
        thClassNames: 'text-data settings-header-space-between-text',
        valuePath: REPORTS_COLUMNS.FREQUENCY,
        displayValuePath: 'reportGroup',
        isSortable: true,
        textAlign: 'left',
        columnKey: REPORTS_COLUMNS.FREQUENCY,
      },
      {
        name: this.intl.t(`reports.scheduled_reports.scheduled_by`),
        tdComponent: SettingsReportsTablesCellsTdScheduledBy,
        thClassNames: 'text-data settings-header-space-between-text',
        valuePath: REPORTS_COLUMNS.CREATED_BY,
        isSortable: true,
        textAlign: 'left',
        columnKey: REPORTS_COLUMNS.CREATED_BY,
      },
      {
        name: this.intl.t(`reports.scheduled_reports.number_of_recipients`),
        tdComponent: SettingsReportsTablesCellsTdNbOfRecipients,
        width: 100,
        minWidth: 100,
        thClassNames: '',
        valuePath: REPORTS_COLUMNS.NR_OF_RECEIVERS,
        displayValuePath: 'reportGroup',
        isSortable: true,
        textAlign: 'center',
        thSortWrapperClassNames: 'line-clamp-2',
        columnKey: REPORTS_COLUMNS.NR_OF_RECEIVERS,
      },
      {
        // CTA column
        name: '',
        tdComponent: SettingsReportsTablesCellsTdCta,
        width: 75,
        minWidth: 75,
        thClassNames: '',
        valuePath: REPORTS_COLUMNS.CTA,
        isSortable: false,
        textAlign: 'left',
        columnKey: REPORTS_COLUMNS.CTA,
      },
    ];

    tableColumns.forEach((columnObj) => {
      const columnConfiguration = columnObj;

      if (columnObj.isSortable) {
        // @ts-expect-error: Incorrect type
        // Needed for sorting in the controller
        columnConfiguration.sorted = true;
        // @ts-expect-error: Incorrect type
        columnConfiguration.ascending =
          columnObj.columnKey === this.args.sortBy &&
          this.args.sortDirection === 'asc';
      }

      columns.push(columnConfiguration);
    });

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return columns;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get initialSorts() {
    return [
      {
        valuePath: this.args.sortBy,
        isAscending: this.args.sortDirection === 'asc',
      },
    ];
  }

  // eslint-disable-next-line ember/use-brace-expansion
  @computed('args.doRefresh', 'args.model.[]')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get rows() {
    // @ts-expect-error: Incorrect type
    const rows = [];

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.args.model.forEach((reportGroup) => {
      const rowConfiguration = {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        [REPORTS_COLUMNS.SELECT_FOR_DELETE]: reportGroup,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        [REPORTS_COLUMNS.NAME]: reportGroup?.hotel?.id,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        [REPORTS_COLUMNS.TYPE]: reportGroup[REPORTS_COLUMNS.TYPE],
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        [REPORTS_COLUMNS.FILTERS]: reportGroup[REPORTS_COLUMNS.FILTERS],
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        [REPORTS_COLUMNS.FREQUENCY]: reportGroup[REPORTS_COLUMNS.FREQUENCY],
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        [REPORTS_COLUMNS.CREATED_BY]: reportGroup[REPORTS_COLUMNS.SCHEDULED_BY],
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        [REPORTS_COLUMNS.NR_OF_RECEIVERS]: reportGroup?.reports?.length,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        [REPORTS_COLUMNS.CTA]: reportGroup,
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        doRefresh: this.args.doRefresh,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        reportGroup,
      };

      rows.push(rowConfiguration);
    });

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return rows;
  }

  convertToRowObjects(): void {
    const reportgroups = this.args.model;
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const adaptedModel = [...this.convertedModel, ...reportgroups];

    // @ts-expect-error: Incorrect type
    if (reportgroups.length > 0 && reportgroups.length < adaptedModel.length) {
      adaptedModel.splice(
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        reportgroups.length,
        // @ts-expect-error: Incorrect type
        adaptedModel.length - reportgroups.length,
      );
    }
  }

  @action
  // @ts-expect-error: Incorrect type
  onTableSort(sorts): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    const columnKey = sorts[0].valuePath.split('.')[0];
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const ascending = sorts[0].isAscending;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const column = this.columns.find((col) => col.columnKey === columnKey);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    column.ascending = ascending;

    // @ts-expect-error: Incorrect type
    this.args.sortResults(column);
  }

  <template>
    <div class="{{@name}} flex-1">
      <PrismTable
        @checkboxSelectionMode="multiple"
        @columns={{this.columns}}
        @enableAutoSortsUpdate={{true}}
        {{! @glint-expect-error: Incorrect type }}
        @initialSorts={{this.initialSorts}}
        {{! @glint-expect-error: Incorrect type }}
        @isLoading={{@isLoading}}
        {{! @glint-expect-error: Incorrect type }}
        @onRowSelect={{@setRowSelection}}
        @onTableSort={{this.onTableSort}}
        {{! @glint-expect-error: Incorrect type }}
        @overlayMessage={{@message.message}}
        @removeTableBorder={{true}}
        {{! @glint-expect-error: Incorrect type }}
        @rowSelection={{unless @isLoading @rowSelection}}
        @rows={{this.rows}}
        {{! @glint-expect-error: Incorrect type }}
        @widthConstraint="lt-container"
      />
    </div>
  </template>
}
